// jweService.js
import { importX509, GeneralEncrypt, compactDecrypt, importJWK, base64url } from 'jose';
import { get } from 'react-hook-form';
import { getEnv } from '../env';

class JWEService {

  generateRandomKey() {

    const crypto = window.crypto;
    if (crypto && crypto.getRandomValues) {
      const randomValues = new Uint8Array(32);
      crypto.getRandomValues(randomValues);

      const keyHex = Array.from(randomValues)
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('');
        
      return keyHex;
    } else {
      throw new Error('Secure random number generator not available in this environment.');
    }
  }

  async enCryptKey(AESKey) {
    // if (!AESKey) AESKey = this.generateRandomKey();



    console.log('enCryptKey:', AESKey);
    const _data = JSON.stringify({ aeskey: AESKey });
    const plaintext = new TextEncoder().encode(_data);

    // Build certificate from runtime env each call to avoid race/timing issues
    const pemBodyRaw = getEnv('REACT_APP_PUBLIC_KEY', '') || '';
    const pemBody = (typeof pemBodyRaw === 'string')
      ? pemBodyRaw.replace(/\\n/g, '\n').trim()
      : '';
    if (!pemBody) {
      throw new TypeError('Missing REACT_APP_PUBLIC_KEY');
    }
    const cert = `-----BEGIN CERTIFICATE-----\n${pemBody}\n-----END CERTIFICATE-----`;
    const alg = 'RSA-OAEP-256';
    const publicKey = await importX509(cert, alg);
    const generalJwe = await new GeneralEncrypt(plaintext)
      .setProtectedHeader({
        enc: 'A256GCM',
        alg: 'RSA-OAEP-256',
        typ: 'JWE',
        kid: getEnv('REACT_APP_KID'),
        iat: Math.floor(Date.now() / 1000),
      })
      .addRecipient(publicKey)
      .encrypt();

    return this.jwe2EncryptedKey(generalJwe);
  }

  jwe2EncryptedKey(generalJwe) {
    const arr = [
      generalJwe.protected,
      generalJwe.recipients[0].encrypted_key,
      generalJwe.iv,
      generalJwe.ciphertext,
      generalJwe.tag,
    ];
    return arr.join('.');
  }

  hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes;
  }

  isJSONString(str) {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }

  async enCryptData(AESKey, payload) {
    if (!AESKey) {
      console.error('Please get AESKey');
      return Promise.reject(false);
    }
    console.log('enCryptData:', AESKey);
    let plaintext;
    if (typeof payload === 'string' && this.isJSONString(payload)) {
      plaintext = new TextEncoder().encode(JSON.stringify(JSON.parse(payload)));
    } else {
      plaintext = new TextEncoder().encode(JSON.stringify(payload));
    }

    const keyBytes = this.hexToBytes(AESKey);
    const k = base64url.encode(keyBytes);

    const jwk = await importJWK({
      kty: 'oct',
      k,
    }, 'A128GCM');

    const generalJwe = await new GeneralEncrypt(plaintext)
      .setProtectedHeader({
        enc: 'A128GCM',
        alg: 'A256KW',
        typ: 'JWE',
        kid: getEnv('REACT_APP_KID'),
        iat: Math.floor(Date.now() / 1000),
      })
      .addRecipient(jwk)
      .encrypt();

    return {
      recipients: [
        {
          header: {},
          encrypted_key: generalJwe?.recipients[0]?.encrypted_key
        }
      ],
      protected: generalJwe.protected,
      ciphertext: generalJwe.ciphertext,
      iv: generalJwe.iv,
      tag: generalJwe.tag
    };
  }

  async decryptData(AESKey, strJwe) {
    if (!AESKey) {
      console.error('Please get AESKey');
      return Promise.resolve(false);
    }
    const keyBytes = this.hexToBytes(AESKey);
    const k = base64url.encode(keyBytes);
    const jwk = await importJWK({
      kty: 'oct',
      k,
    }, 'A128GCM');

    const { plaintext } = await compactDecrypt(strJwe, jwk);
    const strPayload = new TextDecoder().decode(plaintext);
    const payload = JSON.parse(strPayload);

    if (payload.iss) {
      payload.iss = JSON.parse(payload.iss);
    }

    return payload;
  }

  async getEncryptGetRequest() {
    const encrypted_key = await this.enCryptKey();
    return { encrypted_key };
  }

  async getEncryptPostRequest(AESKey, payload) {
    const encrypted_key = await this.enCryptKey(AESKey);
    const encrypted_data = await this.enCryptData(AESKey, payload);
    return { encrypted_key, encrypted_data };
  }
}

export default new JWEService();
